/**
 * MemberProfileHub — the Omat tiedot hub landing (T027).
 *
 * Composes the member identity block (MemberProfileCard — avatar, name, member
 * number, and the membership "alkaen" line grouped together) and three
 * entry-point buttons that swap the in-page view to a section:
 *
 *   - Omat yhteystiedot  -> contact details (read-only)
 *   - Jäsenmaksu         -> membership fee
 *   - Jäsentodistus      -> membership certificate
 *
 * Grounded in the proto's Profile hub (identity + 3 stacked entry
 * rows with a trailing chevron). The proto navigates to separate routes; per the
 * lead-decided IA we keep a single /profile route and swap the view in place, so
 * each entry point is a <button> calling a parent-supplied select callback (the
 * parent owns the view state). The buttons are real, keyboard-operable controls
 * with a chevron affordance reading "this row leads onward".
 *
 * No raw colour literals (FR-013) — token classes only. Mobile-first, 375 px
 * safe (FR-012).
 *
 * Owned by feature 026-proto-convergence-v2.
 * Task: T027
 */

import type { JSX } from 'react';
import { useOptionalAnalyticsConsent } from '../../analytics/AnalyticsConsentProvider.js';
import { useTranslate } from '../../i18n/useTranslate.js';
import type { MemberProfile } from '../../member-profile/MemberProfilePort.js';
import { useCapabilityAllowed } from '../../membership/useCapability.js';
import {
  ChartBarIcon,
  ChevronRightIcon,
  DocumentIcon,
  ProfileIcon,
  ShieldIcon,
  WalletIcon,
} from '../../shell/icons.js';
import { MemberProfileCard } from './MemberProfileCard.js';

export interface MemberProfileHubProps {
  readonly displayName: string;
  readonly profile: MemberProfile;
  readonly onSelectContact: () => void;
  readonly onSelectFee: () => void;
  readonly onSelectCertificate: () => void;
  readonly onSelectMyData: () => void;
  /** Optional because isolated profile-hub tests omit the analytics provider. */
  readonly onSelectAnalyticsConsent?: () => void;
}

interface EntryPointProps {
  readonly icon: JSX.Element;
  readonly label: string;
  readonly onSelect: () => void;
  /**
   * Membership-gated disabled state (028-membership-state-gating, AC4 / FR-010).
   * When `true`, the entry is greyed and `aria-disabled` so the disabled state
   * is perceivable AND conveyed to assistive tech; activation is suppressed. The
   * row stays focusable (kept in the tab order, announced as dimmed) rather than
   * being removed, per FR-010 "not silently removed".
   */
  readonly disabled?: boolean;
}

function EntryPoint({ icon, label, onSelect, disabled = false }: EntryPointProps): JSX.Element {
  // Rows live inside the shared container (proto Profile.tsx:267); each carries a
  // bottom separator (last row drops it) instead of its own box border. Hover is
  // the proto's light-green wash, token-backed via the primary tint (the binding
  // proto hex `#D8EDD0` would be a no-token-bypass violation in a component).
  const stateClasses = disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-primary/10';
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onSelect}
      aria-disabled={disabled || undefined}
      className={`flex w-full items-center justify-between gap-3 border-b border-border px-4 py-4 text-left text-foreground transition-colors last:border-b-0 ${stateClasses}`}
    >
      <span className="flex items-center gap-3">
        {icon}
        <span className="font-medium">{label}</span>
      </span>
      <ChevronRightIcon className="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden="true" />
    </button>
  );
}

export function MemberProfileHub({
  displayName,
  profile,
  onSelectContact,
  onSelectFee,
  onSelectCertificate,
  onSelectMyData,
  onSelectAnalyticsConsent,
}: MemberProfileHubProps): JSX.Element {
  const t = useTranslate();
  const analyticsConsent = useOptionalAnalyticsConsent();
  // Membership-capability gating (042-membership-access-binary, D-202): a valid
  // member sees every entry active. Under binary access only a `valid` member
  // reaches this hub (an ended membership is refused a session and never enters
  // the shell); the greying below is the fail-closed backstop for the degenerate
  // `none` case, where the `other_actions` entries grey (Jäsenmaksu/myData stay).
  const otherActionsAllowed = useCapabilityAllowed('other_actions');
  const analyticsConsentAvailable = analyticsConsent?.resolution.kind === 'ready';

  // Hero sheet: the proto's full-bleed panel (proto Profile.tsx:225-232). A
  // rounded-TOP curve (rounded-t-hero) over a square bottom that fills to the
  // bottom nav — NOT a floating card. It breaks out of the shell's content insets
  // to span the frame: `-mx-6` cancels the two horizontal px-4 (main + section);
  // `-mb-8` cancels the bottom insets (section pb-6 + content column py-4) so the
  // sheet reaches the nav with no grey strip. `flex-1` stretches it down the
  // flex-col chain (main -> content column -> section -> here). The avatar
  // overlaps the top edge (proto :227,244); mt-10 supplies the gap its negative
  // top margin lifts into.
  return (
    <div className="-mx-6 -mb-8 mt-10 flex flex-1 flex-col gap-4 rounded-t-hero bg-card px-8 pb-8 shadow-2xl">
      <MemberProfileCard displayName={displayName} profile={profile} />

      {/* The profile entry rows share ONE bordered, rounded-2xl container with row
          separators — the proto's single entry panel, not three separate boxes.
          Capped at max-w-sm and centred (proto Profile.tsx:266) so the rows keep
          the proto's generous side margin inside the full-bleed sheet, not
          stretching edge-to-edge. mt-5 adds to the sheet's gap-4 for a 40px
          section break above the actions (proto's pt-40 spacing), so the group
          does not read as crammed under the identity block. */}
      <div className="mx-auto mt-5 flex w-full max-w-sm flex-col overflow-hidden rounded-2xl border border-border">
        <EntryPoint
          icon={<ProfileIcon className="h-5 w-5 shrink-0 text-foreground" aria-hidden="true" />}
          label={t('profile.hub.contact')}
          onSelect={onSelectContact}
          disabled={!otherActionsAllowed}
        />
        <EntryPoint
          icon={<WalletIcon className="h-5 w-5 shrink-0 text-foreground" aria-hidden="true" />}
          label={t('profile.hub.fee')}
          onSelect={onSelectFee}
        />
        <EntryPoint
          icon={<DocumentIcon className="h-5 w-5 shrink-0 text-foreground" aria-hidden="true" />}
          label={t('profile.hub.certificate')}
          onSelect={onSelectCertificate}
          disabled={!otherActionsAllowed}
        />
        {/* Tallennetut tiedot (033 US1): the GDPR right of access belongs to
            every data subject, so this entry is NOT membership-gated — it stays
            active even in the degenerate/non-active member case (FR-001). */}
        <EntryPoint
          icon={<ShieldIcon className="h-5 w-5 shrink-0 text-foreground" aria-hidden="true" />}
          label={t('profile.hub.myData')}
          onSelect={onSelectMyData}
        />
        {onSelectAnalyticsConsent !== undefined && analyticsConsentAvailable && (
          <EntryPoint
            icon={<ChartBarIcon className="h-5 w-5 shrink-0 text-foreground" aria-hidden="true" />}
            label={t('analytics.consent.profile.heading')}
            onSelect={onSelectAnalyticsConsent}
          />
        )}
      </div>
    </div>
  );
}
